import * as React from 'react';
import { Users } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableCellStatus,
  TableEmptyState,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import type { Attendee, MeetingAnalytics } from '../types';
import { durationOrDash } from '../format';
import { Section } from './Section';

/** Who attended, their role, how long they were present, and whether they're
 *  still connected. The present-time meter (present ÷ total call duration) is the
 *  view's one deliberate flourish — a reviewer sees who stayed vs. who dropped. */
export function AttendanceTable({ data }: { data: MeetingAnalytics }) {
  return (
    <Section
      title="Attendance"
      icon={<Users className="h-4 w-4" aria-hidden="true" />}
      aside={
        <span className="text-xs font-medium tabular-nums text-[var(--text-muted)]">
          {data.attendance.length} {data.attendance.length === 1 ? 'person' : 'people'}
        </span>
      }
      bodyClassName="p-0"
    >
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Participant</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Present</TableHead>
            <TableHead align="right">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.attendance.length === 0 ? (
            <TableEmptyState
              colSpan={4}
              icon={<Users className="h-6 w-6 text-[var(--text-disabled)]" aria-hidden="true" />}
              title="No attendance recorded"
              description="No one has joined this meeting yet."
            />
          ) : (
            data.attendance.map((a, i) => (
              <AttendanceRow
                key={`${a.identity || 'guest'}-${i}`}
                attendee={a}
                totalSeconds={data.durationSeconds}
              />
            ))
          )}
        </TableBody>
      </Table>
    </Section>
  );
}

function AttendanceRow({ attendee, totalSeconds }: { attendee: Attendee; totalSeconds: number }) {
  const isHost = attendee.role === 'salesperson';
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell>
        <span className="font-medium text-[var(--text-primary)]">
          {attendee.identity || 'Guest'}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant={isHost ? 'soft-violet' : 'soft-blue'} size="sm">
          {isHost ? 'Salesperson' : 'Lead'}
        </Badge>
      </TableCell>
      <TableCell>
        <PresenceMeter present={attendee.presentSeconds} total={totalSeconds} />
      </TableCell>
      <TableCell align="right">
        {attendee.stillJoined ? (
          <TableCellStatus variant="success">In call</TableCellStatus>
        ) : (
          <TableCellStatus variant="neutral">Left</TableCellStatus>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Present time + a proportional bar vs. the whole call. The bar is only drawn
 *  when there's a real denominator (duration > 0); its width is data, not a
 *  hardcoded style. */
function PresenceMeter({ present, total }: { present: number; total: number }) {
  const ratio = total > 0 ? Math.min(1, Math.max(0, present / total)) : null;
  return (
    <div className="flex items-center gap-2.5">
      <span className="tabular-nums text-sm text-[var(--text-primary)]">
        {durationOrDash(present)}
      </span>
      {ratio != null && (
        <span
          className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-[var(--muted)]"
          role="img"
          aria-label={`${Math.round(ratio * 100)}% of the call`}
        >
          <span
            className="block h-full rounded-full bg-[var(--primary)]"
            style={{ width: `${ratio * 100}%` }}
          />
        </span>
      )}
    </div>
  );
}
